import { z } from "zod";
import {
  ContentSectionType,
  CTA_TYPE_BUTTON,
  CTA_TYPE_BUTTON_OVERLAY,
  CTA_TYPE_CONDITIONAL_CTA,
} from "@/types/contentSectionType";
import {
  buttonFieldsSchema,
  buttonOverlayFieldsSchema,
  cellSectionFieldsSchema,
  chartContentFieldsSchema,
  columnsSectionSchema,
  conditionalCtaOptionSchema,
  contentFromSectionFieldsSchema,
  dataDistributionBackgroundSchema,
  dataDistributionContentFieldsSchema,
  gridContentFieldsSchema,
  imageContentFieldsSchema,
  imageSchema,
  monthOverviewFieldsSchema,
  personalizedImageContentFieldsSchema,
  rowSectionSchema,
  tableContentFieldsSchema,
  textContentFieldsSchema,
  valuesSectionSchema,
} from "./contentItemFieldSchemas";
import {
  optionalBoolean,
  optionalNumber,
  optionalString,
  stringFrom,
} from "./schemaPrimitives";

export type Image = z.infer<typeof imageSchema>;

const textContentSchema = textContentFieldsSchema.and(
  z.object({ type: z.literal(ContentSectionType.Text) }),
);
export type TextContent = z.infer<typeof textContentSchema>;

export const parseTextContentFromFields = (
  parsed: z.output<typeof textContentFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof textContentSchema> =>
  textContentSchema.parse({
    type: ContentSectionType.Text,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

const imageContentSchema = imageContentFieldsSchema.and(
  z.object({ type: z.literal(ContentSectionType.Image) }),
);
export type ImageContent = z.infer<typeof imageContentSchema>;

export const parseImageContentFromFields = (
  parsed: z.output<typeof imageContentFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof imageContentSchema> =>
  imageContentSchema.parse({
    type: ContentSectionType.Image,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

const personalizedImageContentSchema = personalizedImageContentFieldsSchema.and(
  z.object({ type: z.literal(ContentSectionType.PersonalizedImage) }),
);
export type PersonalizedImageContent = z.infer<
  typeof personalizedImageContentSchema
>;

export const parsePersonalizedImageContentFromFields = (
  parsed: z.output<typeof personalizedImageContentFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof personalizedImageContentSchema> =>
  personalizedImageContentSchema.parse({
    type: ContentSectionType.PersonalizedImage,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

export const contentFromSectionSchema = contentFromSectionFieldsSchema.and(
  z.object({ type: z.literal(ContentSectionType.Unknown) }),
);
export type ContentFromSection = z.infer<typeof contentFromSectionSchema>;

export type ColumnsSection = z.output<typeof columnsSectionSchema>;
export type ValuesSection = z.output<typeof valuesSectionSchema>;
export type RowSection = z.output<typeof rowSectionSchema>;

const tableContentSchema = tableContentFieldsSchema.and(
  z.object({ type: z.literal(ContentSectionType.Table) }),
);
export type TableContent = z.output<typeof tableContentSchema>;

export const parseTableContentFromFields = (
  parsed: z.output<typeof tableContentFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof tableContentSchema> =>
  tableContentSchema.parse({
    type: ContentSectionType.Table,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

const chartContentSchema = chartContentFieldsSchema.and(
  z.object({ type: z.literal(ContentSectionType.Chart) }),
);
export type ChartContent = z.infer<typeof chartContentSchema>;

export const parseChartContentFromFields = (
  parsed: z.output<typeof chartContentFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof chartContentSchema> =>
  chartContentSchema.parse({
    type: ContentSectionType.Chart,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

const dataDistributionContentSchema = z.object({
  type: z.literal(ContentSectionType.DataDistribution),
  contentFrom: stringFrom(""),
  valueFormatPattern: stringFrom(""),
  formLabel: stringFrom(""),
  label: imageContentSchema,
  question: textContentSchema.optional(),
  background: dataDistributionBackgroundSchema.optional(),
});
export type DataDistributionContent = z.infer<
  typeof dataDistributionContentSchema
>;

export const parseDataDistributionContentFromFields = (
  parsed: z.output<typeof dataDistributionContentFieldsSchema>,
  formLabelOverride: string | undefined,
  label: z.output<typeof imageContentSchema>,
  question: z.output<typeof textContentSchema> | undefined,
  background: z.output<typeof dataDistributionBackgroundSchema> | undefined,
): z.output<typeof dataDistributionContentSchema> =>
  dataDistributionContentSchema.parse({
    type: ContentSectionType.DataDistribution,
    contentFrom: parsed.contentFrom,
    valueFormatPattern: parsed.valueFormatPattern,
    formLabel: formLabelOverride ?? parsed.formLabel,
    label,
    question,
    background,
  });

const buttonSchema = buttonFieldsSchema.and(
  z.object({ type: z.literal(CTA_TYPE_BUTTON) }),
);
export type Button = z.output<typeof buttonSchema>;

export const parseButtonFromFields = (
  parsed: z.output<typeof buttonFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof buttonSchema> =>
  buttonSchema.parse({
    type: CTA_TYPE_BUTTON,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

const buttonOverlaySchema = buttonOverlayFieldsSchema.and(
  z.object({ type: z.literal(CTA_TYPE_BUTTON_OVERLAY) }),
);
export type ButtonOverlay = z.output<typeof buttonOverlaySchema>;

export const parseButtonOverlayFromFields = (
  parsed: z.output<typeof buttonOverlayFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof buttonOverlaySchema> =>
  buttonOverlaySchema.parse({
    type: CTA_TYPE_BUTTON_OVERLAY,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

const conditionalCtaContentSchema = z.object({
  type: z.literal(CTA_TYPE_CONDITIONAL_CTA),
  formLabel: stringFrom(""),
  options: z.array(conditionalCtaOptionSchema),
});
export type ConditionalCtaContent = z.infer<typeof conditionalCtaContentSchema>;

export const parseConditionalCtaFromFields = (
  parsed: {
    formLabel: string;
    options: z.output<typeof conditionalCtaOptionSchema>[];
  },
  formLabelOverride?: string,
): ConditionalCtaContent =>
  conditionalCtaContentSchema.parse({
    type: CTA_TYPE_CONDITIONAL_CTA,
    formLabel: formLabelOverride ?? parsed.formLabel,
    options: parsed.options,
  });

const cellContentSchema = z.union([
  textContentSchema,
  imageContentSchema,
  personalizedImageContentSchema,
]);
export type CellContent = z.infer<typeof cellContentSchema>;

const cellSectionFullSchema = z.object({
  conditionalOnVariable: optionalString.optional(),
  content: z.array(cellContentSchema),
});
export type CellSection = z.infer<typeof cellSectionFullSchema>;

export const parseCellSectionFromFields = (
  parsed: z.output<typeof cellSectionFieldsSchema>,
  content: z.output<typeof cellContentSchema>[],
): z.output<typeof cellSectionFullSchema> =>
  cellSectionFullSchema.parse({
    conditionalOnVariable: parsed.conditionalOnVariable,
    content,
  });

const gridContentSchema = z.object({
  type: z.literal(ContentSectionType.Grid),
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
  nbColumns: optionalNumber.optional(),
  hideBorder: optionalBoolean.optional(),
  cells: z.array(cellSectionFullSchema),
  cell: cellSectionFullSchema.optional(),
});
export type GridContent = z.infer<typeof gridContentSchema>;

export const parseGridContentFromFields = (
  parsed: z.output<typeof gridContentFieldsSchema>,
  formLabelOverride: string | undefined,
  cells: z.output<typeof cellSectionFullSchema>[],
): z.output<typeof gridContentSchema> =>
  gridContentSchema.parse({
    type: ContentSectionType.Grid,
    contentFrom: parsed.contentFrom,
    formLabel: formLabelOverride ?? parsed.formLabel,
    nbColumns: parsed.nbColumns,
    hideBorder: parsed.hideBorder,
    cells,
  });

const monthOverviewSchema = monthOverviewFieldsSchema.and(
  z.object({ type: z.literal(ContentSectionType.MonthOverview) }),
);
export type MonthOverview = z.infer<typeof monthOverviewSchema>;

export const parseMonthOverviewFromFields = (
  parsed: z.output<typeof monthOverviewFieldsSchema>,
  formLabelOverride?: string,
): z.output<typeof monthOverviewSchema> =>
  monthOverviewSchema.parse({
    type: ContentSectionType.MonthOverview,
    ...parsed,
    formLabel: formLabelOverride ?? parsed.formLabel,
  });

const paragraph1Schema = z.object({
  default: textContentSchema.optional(),
  positiveWithSavings: textContentSchema.optional(),
  positiveWithPerfectSavings: textContentSchema.optional(),
  positiveWithNegativeSavings: textContentSchema.optional(),
  positiveWithNoSavings: textContentSchema.optional(),
  negativeWthNegativeSavings: textContentSchema.optional(),
});
export type Paragraph1 = z.infer<typeof paragraph1Schema>;

const paragraph2Schema = z.object({
  loanAndCreditCard: textContentSchema.optional(),
  onlyCreditCard: textContentSchema.optional(),
  onlyLoan: textContentSchema.optional(),
});
export type Paragraph2 = z.infer<typeof paragraph2Schema>;

const monthOverviewDetailsSchema = z.object({
  type: z.literal(ContentSectionType.MonthOverviewDetails),
  contentFrom: stringFrom(""),
  monthVariable: stringFrom(""),
  formLabel: stringFrom(""),
  paragraph1: paragraph1Schema,
  paragraph2: paragraph2Schema,
  paragraph3: textContentSchema.optional(),
});
export type MonthOverviewDetails = z.infer<typeof monthOverviewDetailsSchema>;

const moneyInSchema = z.object({
  other: textContentSchema.optional(),
  loan: textContentSchema.optional(),
  otherAndLoan: textContentSchema.optional(),
  zero: textContentSchema.optional(),
});
export type MoneyIn = z.infer<typeof moneyInSchema>;

const moneyOutSchema = z.object({
  other: textContentSchema.optional(),
  creditCard: textContentSchema.optional(),
  loan: textContentSchema.optional(),
  otherAndLoan: textContentSchema.optional(),
  otherAndCreditCard: textContentSchema.optional(),
  loanAndCreditCard: textContentSchema.optional(),
  otherAndLoanAndCreditCard: textContentSchema.optional(),
  zero: textContentSchema.optional(),
});
export type MoneyOut = z.infer<typeof moneyOutSchema>;

const creditCardSchema = z.object({
  negativeTransactions: textContentSchema.optional(),
  payments: textContentSchema.optional(),
  paymentsAndNegativeTransactions: textContentSchema.optional(),
});
export type CreditCard = z.infer<typeof creditCardSchema>;

const savingsSchema = z.object({
  totalAndCertificate: textContentSchema.optional(),
  negativeTotalAndNegativeCertificate: textContentSchema.optional(),
  totalAndSavings: textContentSchema.optional(),
  negativeTotalAndNegativeSavings: textContentSchema.optional(),
  totalAndIRA: textContentSchema.optional(),
  negativeTotalAndNegativeIRA: textContentSchema.optional(),
  totalAndSavingsAndCertificate: textContentSchema.optional(),
  totalAndNegativeSavingsAndCertificate: textContentSchema.optional(),
  negativeTotalAndNegativeSavingsAndCertificate: textContentSchema.optional(),
  totalAndSavingsAndNegativeCertificate: textContentSchema.optional(),
  negativeTotalAndNegativeSavingsAndNegativeCertificate:
    textContentSchema.optional(),
  totalAndCertificateAndIRA: textContentSchema.optional(),
  totalAndCertificateAndNegativeIRA: textContentSchema.optional(),
  negativeTotalAndCertificateAndNegativeIRA: textContentSchema.optional(),
  totalAndNegativeCertificateAndIRA: textContentSchema.optional(),
  negativeTotalAndNegativeCertificateAndIRA: textContentSchema.optional(),
  negativeTotalAndNegativeCertificateAndNegativeIRA:
    textContentSchema.optional(),
  totalAndSavingsAndNegativeIRA: textContentSchema.optional(),
  negativeTotalAndSavingsAndNegativeIRA: textContentSchema.optional(),
  totalAndSavingsAndIRA: textContentSchema.optional(),
  totalAndNegativeSavingsAndIRA: textContentSchema.optional(),
  totalAndSavingsAndCertificateAndNegativeIRA: textContentSchema.optional(),
  negativeTotalAndSavingsAndCertificateAndNegativeIRA:
    textContentSchema.optional(),
  totalAndSavingsAndCertificateAndIRA: textContentSchema.optional(),
  totalAndNegativeSavingsAndCertificateAndIRA: textContentSchema.optional(),
  negativeTotalAndSavingsAndNegativeCertificateAndIRA:
    textContentSchema.optional(),
  totalAndSavingsAndNegativeCertificateAndIRA: textContentSchema.optional(),
  negativeTotalAndSavingsAndNegativeCertificateAndNegativeIRA:
    textContentSchema.optional(),
  totalAndSavingsAndNegativeCertificateAndNegativeIRA:
    textContentSchema.optional(),
});
export type Savings = z.infer<typeof savingsSchema>;

const monthlyOverviewSchema = z.object({
  type: z.literal(ContentSectionType.MonthlyOverview),
  contentFrom: stringFrom(""),
  formLabel: stringFrom(""),
  moneyIn: moneyInSchema,
  moneyOut: moneyOutSchema,
  creditCard: creditCardSchema.optional(),
  savings: savingsSchema.optional(),
});
export type MonthlyOverview = z.infer<typeof monthlyOverviewSchema>;

export const contentItemSchema = z.union([
  imageContentSchema,
  personalizedImageContentSchema,
  buttonOverlaySchema,
  buttonSchema,
  conditionalCtaContentSchema,
  tableContentSchema,
  gridContentSchema,
  chartContentSchema,
  monthlyOverviewSchema,
  monthOverviewSchema,
  monthOverviewDetailsSchema,
  dataDistributionContentSchema,
  contentFromSectionSchema,
  textContentSchema,
]);
export type ContentItem = z.infer<typeof contentItemSchema>;

export type ContentArray = ContentItem[];

export const advisorContentSchema = z.object({
  summary: textContentSchema.optional(),
});
export type AdvisorContent = z.infer<typeof advisorContentSchema>;
